import { CustomLayers } from "ags/names";
import { Signal } from "signals";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import particleManager from "slotworks/graphics/particles/particle-manager";
import soundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import { RandomBoolean, RandomRangeInt } from "slotworks/utils/math/random";
import Timer from "slotworks/utils/timer";
import { Easing, Interpolation, Tween } from "slotworks/utils/tween";

export default class SparkComponent {

    public static tweenSpeed = 800;

    public onParticlesGone: Signal;

    protected emitter: any;
    protected emitterId: number;
    protected emitterPosition = new Point();
    protected fireworkLayer: Layers = null;
    protected sparkLayer: Layers = null;
    protected sparkSprite: AnimatedSprite = null;
    protected symbol: SymbolComponent;
    protected container: PIXI.Container;
    protected destination: Point;

    public constructor(symbol: SymbolComponent, destination: Point, layer: Layers) {
        this.symbol = symbol;
        this.destination = destination;

        this.sparkLayer = Layers.Custom.get(CustomLayers.FIREWORK_SPARK);
        this.fireworkLayer = layer;
        this.container = new PIXI.Container();
        this.sparkLayer.container.addChild(this.container);
        this.onParticlesGone = new Signal();

        this.sparkSprite = this.getSpark();
        this.container.addChild(this.sparkSprite);
        this.sparkSprite.play();

        this.setEmitter();
    }

    public fire(mute = false) {
        this.moveSparkLayerToTop();

        if (!mute) {
            soundManager.customEvent(`spark1`);
        }

        return new Contract<void>((resolve) => {

            const tween = this.generateSparkTween();

            tween.onComplete(() => {
                this.sparkSprite.visible = false;
                this.emitter.emit = false;

                resolve(null);
                if (!mute) {
                    soundManager.customEvent(`spark2`);
                }

                // Wait for particles to die.
                Timer.setTimeout(() => {
                    this.onParticlesGone.dispatch();
                }, 800);
            });
        });
    }

    public destroy() {
        particleManager.remove(this.emitterId);
        this.sparkSprite.destroy();
        this.container.destroy();
    }

    public setVisible(isVisible = true) {
        this.container.visible = isVisible;
    }

    protected getSpark(): AnimatedSprite {
        return GraphicsManager.createAnimation("spark");
    }

    protected setEmitter() {
        this.emitterId = particleManager.add("fireworkSpark", { x: 0, y: 0 } as Point, this.container);
        this.emitter = particleManager.get(this.emitterId);
    }

    protected setEmitterPosition(position: Point) {
        this.sparkSprite.anchor.set(0.5);
        this.sparkSprite.position.set(position.x, position.y);
        this.emitter.emit = true;

        this.emitter.updateSpawnPos(position.x, position.y);
        this.emitter.resetPositionTracking();

        this.emitterPosition.x = position.x;
        this.emitterPosition.y = position.y;
    }

    protected getSymbolPosition() {
        const position = Layers.MatrixContent.getPosition(`symbol_${this.symbol.gridPosition.x}_${this.symbol.gridPosition.y}`);

        const sparkOffset = Layers.MatrixContent.getPosition("sparkOffset");
        if (sparkOffset) {
            const referenceSymbol = Layers.MatrixContent.getPosition("symbol_1_1");
            const offset = new Point(sparkOffset.x - referenceSymbol.x, sparkOffset.y - referenceSymbol.y);

            position.x += offset.x;
            position.y += offset.y;
        } else {
            position.x += 26 + position.width / 2;
            position.y -= 28;
        }

        const convertedPosition = Layers.MatrixContent.localToLocal(this.sparkLayer, new Point(position.x, position.y));

        return convertedPosition;
    }

    protected generateSparkTween() {
        const position = this.getSymbolPosition();

        this.setEmitterPosition(position);

        const targetPosition = this.fireworkLayer.localToLocal(this.sparkLayer, this.destination);
        const intermediatePosition = this.getIntermediatePoint(position, targetPosition);
        const xPositions = [intermediatePosition.x, targetPosition.x];
        const yPositions = [intermediatePosition.y, targetPosition.y];

        const tween = new Tween(this.emitterPosition);
        tween.to({ x: xPositions, y: yPositions }, SparkComponent.tweenSpeed);
        tween.interpolation(Interpolation.Bezier);
        tween.easing(Easing.Quadratic.In);

        tween.onUpdate(() => {
            this.emitter.updateSpawnPos(this.emitterPosition.x, this.emitterPosition.y);
            this.sparkSprite.x = this.emitterPosition.x;
            this.sparkSprite.y = this.emitterPosition.y;
        });

        tween.start();

        return tween;
    }

    protected getIntermediatePoint(pointA: Point, pointB: Point) {
        const rightAngle = Math.atan2(pointA.y - pointB.y, pointA.x - pointB.x);
        const distance = RandomRangeInt(400, 600);
        const xDistance = Math.sin(rightAngle) + distance * (RandomBoolean() ? 1 : -1);
        const yDistance = Math.cos(rightAngle) + distance * (RandomBoolean() ? 1 : -1);
        return new Point(pointA.x + xDistance, pointA.y + yDistance);
    }

    private moveSparkLayerToTop() {
        this.sparkLayer.container.parent.addChild(this.sparkLayer.container);
    }
}
